import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { API_BASE_URL } from "../config/api";
import { fetchMyMysteryTeams, MysteryTeam, MysteryTeamAccess } from "../api/mysteryTeam";
import { ALL_MYSTERY_TEAMS, setMysteryTeamHeader } from "../helpers/mysteryTeamHeader";
import { useRegionSettings } from "./RegionSettingsProvider";

export const MYSTERY_TEAM_ACCESS_KEY = "mysteryTeamAccess";

export type MysteryTeamContextValue = {
  /** False until the user's teams are known; Mystery Slabs queries wait for it. */
  ready: boolean;
  /** The region has teams. Without them everything works as before teams existed. */
  teamsEnabled: boolean;
  /** Admin: creates teams, assigns staff and batches, and may pick "All teams". */
  canManage: boolean;
  /** Every team for an admin, the user's own for everyone else, in name order. */
  teams: MysteryTeam[];
  /** What each Mystery Slabs request carries: a team id, "all", or null for nothing. */
  selection: string | null;
  /** The one team being worked in, or null ("All teams", or no teams). */
  selectedTeam: MysteryTeam | null;
  isAllTeams: boolean;
  /** The region has teams but this user is in none of them, so they see no mystery slabs. */
  isWithoutTeam: boolean;
  /** Adding cards or opening a batch needs one team to put them in. */
  canAddWork: boolean;
  /** What the sidebar offers, in order: "all" (admins) then each team id. */
  options: string[];
  selectTeam: (value: string) => void;
  teamName: (teamId: number | null | undefined) => string | null;
};

const NO_TEAMS: MysteryTeamContextValue = {
  ready: true,
  teamsEnabled: false,
  canManage: false,
  teams: [],
  selection: null,
  selectedTeam: null,
  isAllTeams: false,
  isWithoutTeam: false,
  canAddWork: true,
  options: [],
  selectTeam: () => undefined,
  teamName: () => null,
};

// Rendered without the provider (most tests), Mystery Slabs behaves as a region without teams.
const MysteryTeamContext = createContext<MysteryTeamContextValue>(NO_TEAMS);

export const useMysteryTeam = () => useContext(MysteryTeamContext);

/** The part of every Mystery Slabs query key that keeps one team's cache from serving another's. */
export const useMysteryTeamKey = () => useMysteryTeam().selection ?? "default";

/**
 * The team the user works in: their remembered pick when they may still pick it, else the
 * default the server also falls back to — "All teams" for an admin, the first team for anyone
 * else. Null sends no header: the region has no teams, or the user is in none of them.
 */
export const resolveMysteryTeamSelection = (
  access: MysteryTeamAccess | undefined,
  stored: string | null
): string | null => {
  if (!access?.teamsEnabled) return null;
  const ids = (access.teams ?? []).map((team) => String(team.id));
  if (access.allTeams) {
    return stored && (stored === ALL_MYSTERY_TEAMS || ids.includes(stored)) ? stored : ALL_MYSTERY_TEAMS;
  }
  if (ids.length === 0) return null;
  return stored && ids.includes(stored) ? stored : ids[0];
};

// Per region: each region is its own portal with its own teams.
const REGION = API_BASE_URL.match(/\/api\/([^/]+)\/v1/)?.[1] ?? "default";
const STORAGE_KEY = `pcg.mysteryTeam.${REGION}`;

const readStored = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeStored = (value: string) => {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // a remembered pick is a convenience; the default still works
  }
};

export const MysteryTeamProvider: React.FC = ({ children }) => {
  const { regionSettings, isLoading: settingsLoading } = useRegionSettings();
  const mysteryEnabled = !!regionSettings?.mysterySlabsEnabled;
  const { data: access, isLoading } = useQuery([MYSTERY_TEAM_ACCESS_KEY], fetchMyMysteryTeams, {
    enabled: mysteryEnabled,
    // An admin can move the user between teams at any time; a minute is the most it can lag.
    staleTime: 60 * 1000,
    // An older backend has no endpoint; that means no teams, not a reason to keep asking.
    retry: false,
  });
  const [stored, setStored] = useState<string | null>(readStored);

  const selection = resolveMysteryTeamSelection(access, stored);
  // Set while rendering, not in an effect: children's effects (their queries) run first.
  setMysteryTeamHeader(selection);

  const selectTeam = useCallback((value: string) => {
    setStored(value);
    writeStored(value);
  }, []);

  // Kept stable across team picks: the sidebar builds its menu from these.
  const teams = useMemo(() => (access?.teamsEnabled ? access.teams ?? [] : []), [access]);
  const options = useMemo(
    () => [...(access?.allTeams ? [ALL_MYSTERY_TEAMS] : []), ...teams.map((team) => String(team.id))],
    [access, teams]
  );
  const teamName = useCallback(
    (teamId: number | null | undefined) =>
      teamId == null ? null : teams.find((team) => team.id === teamId)?.name ?? null,
    [teams]
  );

  const value = useMemo<MysteryTeamContextValue>(() => {
    const teamsEnabled = !!access?.teamsEnabled;
    const selectedTeam = teams.find((team) => String(team.id) === selection) ?? null;
    const isAllTeams = teamsEnabled && selection === ALL_MYSTERY_TEAMS;
    return {
      ready: !settingsLoading && (!mysteryEnabled || !isLoading),
      teamsEnabled,
      canManage: !!access?.canManage,
      teams,
      selection,
      selectedTeam,
      isAllTeams,
      isWithoutTeam: teamsEnabled && selection === null,
      canAddWork: !teamsEnabled || selectedTeam !== null,
      options,
      selectTeam,
      teamName,
    };
  }, [access, teams, options, teamName, selection, settingsLoading, mysteryEnabled, isLoading, selectTeam]);

  return <MysteryTeamContext.Provider value={value}>{children}</MysteryTeamContext.Provider>;
};

export default MysteryTeamProvider;
